import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { adminApi } from '../lib/api';

type InvitationInfo = {
  email: string | null;
  phone: string | null;
  name: string | null;
  roleName: string | null;
  expiresAt: string;
};

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [verifyError, setVerifyError] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [loginId, setLoginId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifyError('초대 토큰이 없습니다.');
      setIsVerifying(false);
      return;
    }

    adminApi
      .verifyInvitation(token)
      .then((res) => {
        if (res.success && res.data) {
          setInvitationInfo(res.data);
          if (res.data.name) {
            setName(res.data.name);
          }
        } else {
          setVerifyError(res.error?.message || '유효하지 않은 초대 링크입니다.');
        }
      })
      .catch(() => {
        setVerifyError('유효하지 않거나 만료된 초대 링크입니다.');
      })
      .finally(() => setIsVerifying(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (password !== passwordConfirm) {
      setSubmitError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 8) {
      setSubmitError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }

    // phone 초대인 경우 이메일 또는 loginId 중 하나는 필수
    if (!invitationInfo?.email && !email && !loginId) {
      setSubmitError('이메일 또는 사용자 지정 ID 중 하나를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await adminApi.acceptInvitation(token, {
        password,
        name,
        phone,
        email: email || undefined,
        loginId: loginId || undefined,
      });
      if (res.success) {
        setIsDone(true);
      } else {
        setSubmitError(res.error?.message || '계정 등록에 실패했습니다.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '계정 등록 중 오류가 발생했습니다.';
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">초대 링크 확인 중...</div>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-card-lg shadow-md text-center max-w-md w-full">
          <h1 className="text-xl font-bold text-red-600 mb-3">유효하지 않은 초대</h1>
          <p className="text-gray-600 mb-6">{verifyError}</p>
          <Button variant="primary" onClick={() => navigate('/login')}>
            로그인 페이지로
          </Button>
        </div>
      </div>
    );
  }

  if (isDone) {
    const hasEmail = invitationInfo?.email || email;
    const hasLoginId = !!loginId;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-card-lg shadow-md text-center max-w-md w-full">
          <h1 className="text-xl font-bold text-gray-900 mb-3">계정 등록 완료</h1>
          <p className="text-gray-600 mb-2">
            슈퍼관리자 계정이 성공적으로 등록되었습니다.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {hasEmail && hasLoginId
              ? '이메일 또는 사용자 지정 ID로 로그인할 수 있습니다.'
              : hasLoginId
                ? '사용자 지정 ID와 비밀번호로 로그인하세요.'
                : '이메일과 비밀번호로 로그인하세요.'}
          </p>
          <Button variant="primary" onClick={() => navigate('/login')}>
            로그인하기
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-card-lg shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">슈퍼관리자 계정 등록</h1>
        <p className="text-sm text-gray-500 mb-6">
          {invitationInfo?.email ? (
            <>초대받은 이메일: <span className="font-medium text-gray-800">{invitationInfo.email}</span></>
          ) : invitationInfo?.phone ? (
            <>초대받은 전화번호: <span className="font-medium text-gray-800">{invitationInfo.phone}</span></>
          ) : null}
          {invitationInfo?.roleName && (
            <>
              {' '}/ 역할:{' '}
              <span className="font-medium text-gray-800">{invitationInfo.roleName}</span>
            </>
          )}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {submitError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              휴대폰 번호 <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="01012345678"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          {/* phone 초대인 경우: 이메일 선택 입력 */}
          {!invitationInfo?.email && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일 (선택)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="이메일로도 로그인하려면 입력"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              사용자 지정 ID (선택)
            </label>
            <input
              type="text"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              placeholder="4자 이상, ID로 로그인 가능"
              minLength={4}
              maxLength={30}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <p className="mt-1 text-xs text-gray-400">사용자 지정 ID를 설정하면 이메일 대신 ID로 로그인할 수 있습니다.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              비밀번호 <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="8자 이상"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              비밀번호 확인 <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              placeholder="비밀번호 재입력"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? '등록 중...' : '계정 등록'}
          </Button>
        </form>
      </div>
    </div>
  );
}
